import React, { useState, useEffect, useRef } from 'react';
import MediaExplorerDataPanel from './MediaExplorerDataPanel';
import { toTitleCase } from '../helpers';
import testOptions from '../../data/testPublications.json';

const defaultPublicationLimit = 15;
const path = window.location.pathname;

const MediaExplorer = ({ publication, setPublication }) => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [options, setOptions] = useState([]);
  const isFirstRender = useRef(true);
  const scrollRef = useRef(null);

  const searchPublication = toTitleCase(publication);
  const media_types = [
    'Newspaper',
    'Magazine',
    'Television',
    'Radio',
    'Website'
  ];

  const getFilteredOptions = (search = '', selectedPublication = '') => {
    let filteredOptions = testOptions.data;

    let selectedOption = null;
    filteredOptions = filteredOptions.filter((option) => {
      if (
        selectedPublication &&
        option.publication_name.toLowerCase() === selectedPublication.toLowerCase()
      ) {
        selectedOption = option.publication_name;
        return false;
      }
      return true;
    });
  
    if (mediaType) {
      filteredOptions = filteredOptions.filter(
        (option) => option.media_type === mediaType
      );
    }
  
    if (search) {
      filteredOptions = filteredOptions.filter((option) =>
        option.publication_name.toLowerCase().includes(search.toLowerCase())
      );
    }
  
    let newOptions = filteredOptions.map((option) => option.publication_name);
  
    if (selectedOption) {
      newOptions.unshift(selectedOption);
    }
  
    return newOptions.slice(0, defaultPublicationLimit);
  };

  useEffect(() => {
    if (!publication) {
      const mediaScopeElement = document.getElementById('media-scope');
      if (mediaScopeElement) {
        setPublication(mediaScopeElement.getAttribute('data-publication'));
      }
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e) => {
      isDown = true;
      el.classList.add('is-dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown = false;
      el.classList.remove('is-dragging');
    };

    const onMouseUp = () => {
      isDown = false;
      el.classList.remove('is-dragging');
    };

    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5; // Adjust sensitivity
      el.scrollLeft = scrollLeft - walk;
    };

    // Add listeners
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);

    // Touch support
    el.addEventListener('touchstart', (e) => {
      isDown = true;
      startX = e.touches[0].pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    });

    el.addEventListener('touchend', () => {
      isDown = false;
    });

    el.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      const x = e.touches[0].pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5;
      el.scrollLeft = scrollLeft - walk;
    });

    // Cleanup
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  useEffect(() => {
    setOptions(getFilteredOptions(search, publication));
  }, [search, publication, mediaType]);
  

  const handleSearch = (e) => {
    setSearch(e.target.value);

    if (e.target.value === '') {
      setOptions(getFilteredOptions('', publication));
    } else {
      setOptions(getFilteredOptions(e.target.value, publication));
    }
  };

  const handleClearSearch = () => {
    setSearch('');
    setOptions(getFilteredOptions('', publication));
  };

  const handlePublicationChange = (option) => {
    // make sure ref is set to false if page was loaded without a publication
    if (isFirstRender.current === true) isFirstRender.current = false;

    const newPublication = option.toLowerCase();

    if (newPublication === publication) {
      return;
    }

    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mediaScopePublicationChange',
      publication: option,
    });

    setPublication(newPublication);
    if ('/marketing-is-about-changing-customer-behavior/' === path) {
      return;
    }
    const newUrl = `${path}${encodeURIComponent(option)}`;
    window.history.replaceState(null, '', newUrl);
  };

  const handleMediaTypeChange = (e) => {
    const selectedMediaType = e.target.value;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mediaScopeMediaTypeChange',
      mediaType: selectedMediaType,
    });
    
    setMediaType(selectedMediaType);
  }

  const handleClearPublication = () => {
    setPublication('');
    if ('/marketing-is-about-changing-customer-behavior/' === path) {
      return;
    }
    const newUrl = `${path}`;
    window.history.replaceState(null, '', newUrl);
  };

  return (
    <>
      <div className="u--padding-top-30 u--padding-bottom-30">
        <div className="media-explorer--header u--text-align-center u--padding-top-30 u--padding-bottom-30">
          <h2 className="h3">
            Discover the most popular readership by news source.
          </h2>
          <p>
            Select a news source and get valuable insights into media
            consumption trends.
          </p>
          <div className="media-explorer--search-input-container">
            <div className="media-explorer--search-input">
              <select
                className="media-explorer--media-type"
                value={mediaType}
                onChange={handleMediaTypeChange}
              >
                <option value="">Media Type</option>
                {media_types.map((media_type) => (
                  <option key={media_type} value={media_type}>
                    {media_type}
                  </option>
                ))}
              </select>
              <div className="media-explorer--publication-search-wrapper">
                <input
                  className="media-explorer--publication-search"
                  type="text"
                  placeholder="Search For a News Source"
                  value={search}
                  onChange={handleSearch}
                />
                {!!search && (
                  <button
                    className="media-explorer--clear-search"
                    onClick={handleClearSearch}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <ul className="media-explorer--publications" data-scroll-ignore ref={scrollRef}>
              {options.map((option) => (
                <>
                  <li
                    key={option}
                    className={
                      publication?.toLowerCase() === option?.toLowerCase()
                        ? 'media-explorer-publication active'
                        : 'media-explorer-publication'
                    }
                    onClick={
                      publication?.toLowerCase() === option?.toLowerCase()
                        ? handleClearPublication
                        : () => handlePublicationChange(option)
                    }
                  >
                    {option}
                    {publication?.toLowerCase() === option?.toLowerCase() && (
                      <button
                        className="media-explorer--clear-publication"
                        onClick={handleClearPublication}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </li>
                </>
              ))}
            </ul>
          </div>
        </div>
        <div className="grid u--margin-top-30">
          <div className="grid__col-xs-12">
            <MediaExplorerDataPanel
              publication={publication}
              loading={loading}
              setLoading={setLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MediaExplorer;
